import { ABOUT } from '../content/about';
import type { Lang, Product } from './api';
import { LANGS, money } from './i18n';
import { localize } from './paths';

/**
 * Что видят поисковики и мессенджеры: заголовок вкладки, описание под ссылкой
 * в выдаче, картинка в превью, адреса языковых версий и разметка товара.
 *
 * Файл без зависимостей от браузера. Им пользуются и витрина (меняет теги при
 * переходах), и сборка (раскладывает готовые страницы, чтобы поисковик увидел
 * текст, не запуская скрипты).
 *
 * ⚠ Армянские тексты — черновой перевод, как и в i18n.ts.
 */

export const SITE_URL = 'https://vishap-tea.com';
export const SITE_NAME = 'Vishap Tea';
/** Картинка для превью ссылки, когда у страницы нет своей. 1200×630 */
export const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

const SAME_AS = ['https://t.me/vishap_tea', 'https://instagram.com/vishap_tea'];

const OG_LOCALE: Record<Lang, string> = { en: 'en_US', ru: 'ru_RU', hy: 'hy_AM' };

interface SeoText {
  homeTitle: string;
  homeDescription: string;
  /** Хвост заголовка карточки: «Название — купить в Ереване | Vishap Tea» */
  buy: string;
  /** Запасное описание товара, когда своего текста нет */
  productFallback: (title: string, category: string | null, price: string | null) => string;
  aboutTitle: string;
  cartTitle: string;
  checkoutTitle: string;
  orderTitle: string;
  notFoundTitle: string;
}

export const SEO_TEXT: Record<Lang, SeoText> = {
  en: {
    homeTitle: 'Vishap Tea — Chinese tea shop in Yerevan: pu-erh, oolong, red and white tea',
    homeDescription:
      'Hand-picked Chinese tea from a personal collection: shu and sheng pu-erh, oolong, GABA, red, white and green tea. Pickup and courier in Yerevan, delivery across Armenia and worldwide.',
    buy: 'buy in Yerevan',
    productFallback: (title, category, price) =>
      `${title}${category ? `, ${category.toLowerCase()}` : ''}${price ? `, from ${price}` : ''}. Hand-picked tea from Vishap Tea in Yerevan, delivery across Armenia and worldwide.`,
    aboutTitle: 'About Vishap Tea — a tea project from Yerevan',
    cartTitle: 'Cart',
    checkoutTitle: 'Checkout',
    orderTitle: 'Order received',
    notFoundTitle: 'Page not found',
  },
  ru: {
    homeTitle: 'Vishap Tea — магазин китайского чая в Ереване: пуэр, улун, красный и белый чай',
    homeDescription:
      'Китайский чай из личной коллекции: шу и шэн пуэр, улун, ГАБА, красный, белый и зелёный чай. Самовывоз и курьер по Еревану, доставка по Армении и по всему миру.',
    buy: 'купить в Ереване',
    productFallback: (title, category, price) =>
      `${title}${category ? `, ${category.toLowerCase()}` : ''}${price ? `, от ${price}` : ''}. Чай из коллекции Vishap Tea в Ереване, доставка по Армении и по всему миру.`,
    aboutTitle: 'О проекте Vishap Tea — чайный проект из Еревана',
    cartTitle: 'Корзина',
    checkoutTitle: 'Оформление заказа',
    orderTitle: 'Заказ принят',
    notFoundTitle: 'Страница не найдена',
  },
  hy: {
    homeTitle: 'Vishap Tea — չինական թեյի խանութ Երևանում՝ պուեր, ուլուն, կարմիր և սպիտակ թեյ',
    homeDescription:
      'Չինական թեյ անձնական հավաքածուից՝ շու և շեն պուեր, ուլուն, ԳԱԲԱ, կարմիր, սպիտակ և կանաչ թեյ։ Առաքում Երևանում, ամբողջ Հայաստանում և աշխարհով մեկ։',
    buy: 'գնել Երևանում',
    productFallback: (title, category, price) =>
      `${title}${category ? `, ${category}` : ''}${price ? `, սկսած ${price}` : ''}։ Թեյ Vishap Tea-ի հավաքածուից, առաքում Երևանում, Հայաստանում և աշխարհով մեկ։`,
    aboutTitle: 'Vishap Tea նախագծի մասին',
    cartTitle: 'Զամբյուղ',
    checkoutTitle: 'Պատվերի ձևակերպում',
    orderTitle: 'Պատվերն ընդունված է',
    notFoundTitle: 'Էջը չի գտնվել',
  },
};

export interface PageMeta {
  lang: Lang;
  title: string;
  description: string;
  /**
   * Путь страницы без языковой приставки: «/», «/tea/x». Из него собираются
   * канонический адрес и ссылки на другие языки. null — страница не для
   * поиска (корзина, заказ): ей ставим noindex
   */
  path: string | null;
  image?: string | null;
  type?: 'website' | 'product';
  jsonLd?: object[];
}

/** Тег для <head>. Сборка превращает его в HTML, витрина — в элемент */
export interface HeadTag {
  tag: 'meta' | 'link' | 'script';
  attrs: Record<string, string>;
  text?: string;
}

export function absoluteUrl(path: string, lang: Lang): string {
  return SITE_URL + localize(path, lang);
}

/** Обрезает по слову, чтобы в выдаче описание не обрывалось на полуслове */
export function clip(text: string, max = 160): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:—-]+$/, '')}…`;
}

export function headTags(meta: PageMeta): HeadTag[] {
  const tags: HeadTag[] = [
    { tag: 'meta', attrs: { name: 'description', content: meta.description } },
  ];

  if (meta.path === null) {
    tags.push({ tag: 'meta', attrs: { name: 'robots', content: 'noindex, follow' } });
    return tags;
  }

  const url = absoluteUrl(meta.path, meta.lang);
  const image = meta.image || DEFAULT_IMAGE;

  tags.push({ tag: 'link', attrs: { rel: 'canonical', href: url } });
  for (const lang of LANGS) {
    tags.push({
      tag: 'link',
      attrs: { rel: 'alternate', hreflang: lang, href: absoluteUrl(meta.path, lang) },
    });
  }
  tags.push({
    tag: 'link',
    attrs: { rel: 'alternate', hreflang: 'x-default', href: absoluteUrl(meta.path, 'en') },
  });

  const og: Array<[string, string]> = [
    ['og:type', meta.type === 'product' ? 'product' : 'website'],
    ['og:site_name', SITE_NAME],
    ['og:title', meta.title],
    ['og:description', meta.description],
    ['og:url', url],
    ['og:image', image],
    ['og:locale', OG_LOCALE[meta.lang]],
    ...LANGS.filter((l) => l !== meta.lang).map(
      (l): [string, string] => ['og:locale:alternate', OG_LOCALE[l]],
    ),
  ];
  for (const [property, content] of og) tags.push({ tag: 'meta', attrs: { property, content } });
  tags.push({ tag: 'meta', attrs: { name: 'twitter:card', content: 'summary_large_image' } });

  for (const data of meta.jsonLd ?? []) {
    tags.push({
      tag: 'script',
      attrs: { type: 'application/ld+json' },
      text: JSON.stringify({ '@context': 'https://schema.org', ...data }),
    });
  }
  return tags;
}

// ─────────────────────────────────────────────────────────────
// Страницы
// ─────────────────────────────────────────────────────────────

const STORE_ID = `${SITE_URL}/#store`;

function storeLd(lang: Lang): object {
  return {
    '@type': 'OnlineStore',
    '@id': STORE_ID,
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo-mark.svg`,
    image: DEFAULT_IMAGE,
    description: SEO_TEXT[lang].homeDescription,
    address: { '@type': 'PostalAddress', addressLocality: 'Yerevan', addressCountry: 'AM' },
    areaServed: ['AM', 'Worldwide'],
    sameAs: SAME_AS,
  };
}

export function homeMeta(lang: Lang): PageMeta {
  const text = SEO_TEXT[lang];
  return {
    lang,
    title: text.homeTitle,
    description: text.homeDescription,
    path: '/',
    jsonLd: [
      storeLd(lang),
      { '@type': 'WebSite', name: SITE_NAME, url: absoluteUrl('/', lang), inLanguage: lang, publisher: { '@id': STORE_ID } },
    ],
  };
}

export function aboutMeta(lang: Lang): PageMeta {
  const about = ABOUT[lang];
  return {
    lang,
    title: SEO_TEXT[lang].aboutTitle,
    description: clip(`${about.lede} ${about.paragraphs[0] ?? ''}`),
    path: '/about',
    image: `${SITE_URL}/about/gor.jpg`,
  };
}

export function productMeta(product: Product, lang: Lang): PageMeta {
  const text = SEO_TEXT[lang];
  const path = `/tea/${product.slug}`;
  const url = absoluteUrl(path, lang);
  const currency = product.currency ?? 'AMD';
  const price = product.priceFrom !== null ? money(product.priceFrom, currency, lang) : null;

  const own = product.short || product.description;
  const description = own
    ? clip(own)
    : clip(text.productFallback(product.title, product.category?.name ?? null, price));

  const images = product.images?.length
    ? product.images.map((i) => i.url)
    : product.image
      ? [product.image]
      : [];

  // Каждая фасовка — отдельное предложение со своей ценой. Доллары приходят
  // в центах, а schema.org ждёт обычную сумму
  const offers = product.variants.map((v) => ({
    '@type': 'Offer',
    name: v.name,
    sku: v.sku,
    price: currency === 'USD' ? v.price / 100 : v.price,
    priceCurrency: currency,
    availability: v.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    itemCondition: 'https://schema.org/NewCondition',
    url,
    seller: { '@id': STORE_ID },
  }));

  const ld: object[] = [
    {
      '@type': 'Product',
      name: product.title,
      description: clip(product.description || product.short || description, 5000),
      ...(images.length ? { image: images } : {}),
      ...(product.category ? { category: product.category.name } : {}),
      sku: product.variants[0]?.sku,
      url,
      ...(offers.length ? { offers } : {}),
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: SITE_NAME, item: absoluteUrl('/', lang) },
        { '@type': 'ListItem', position: 2, name: product.title, item: url },
      ],
    },
  ];

  return {
    lang,
    title: `${product.title} — ${text.buy} | ${SITE_NAME}`,
    description,
    path,
    image: images[0] ?? null,
    type: 'product',
    jsonLd: ld,
  };
}

/** Корзина, оформление, номер заказа, несуществующий адрес — не для поиска */
export function privateMeta(
  lang: Lang,
  page: 'cartTitle' | 'checkoutTitle' | 'orderTitle' | 'notFoundTitle',
): PageMeta {
  return {
    lang,
    title: `${SEO_TEXT[lang][page]} | ${SITE_NAME}`,
    description: SEO_TEXT[lang].homeDescription,
    path: null,
  };
}
